import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragSortIcon, Icon } from '@vgrubs/components';
import { ReactNode } from 'react';

type SortableListProps<T> = {
  items: T[];
  getId: (item: T) => string | number;
  /** Read by screen readers on the handle: "Move <label>". */
  getLabel: (item: T) => string;
  onReorder: (items: T[]) => void;
  /** No handles, nothing moves. */
  disabled?: boolean;
  /** `dragHandle` is null while disabled. Return null to leave an item out. */
  renderItem: (item: T, index: number, dragHandle: ReactNode) => ReactNode;
};

/**
 * A vertical list sorted by a handle each row puts where it likes. No
 * DragOverlay on purpose: the menu modals are positioned with a transform,
 * which would offset a fixed overlay, so the row itself moves instead.
 */
export const SortableList = <T,>({
  items,
  getId,
  getLabel,
  onReorder,
  disabled,
  renderItem,
}: SortableListProps<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = items.findIndex((item) => getId(item) === active.id);
    const to = items.findIndex((item) => getId(item) === over.id);
    onReorder(arrayMove(items, from, to));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map(getId)}
        strategy={verticalListSortingStrategy}
      >
        {items.map((item, i) => (
          <SortableRow
            key={getId(item)}
            id={getId(item)}
            label={getLabel(item)}
            disabled={!!disabled}
            render={(handle) => renderItem(item, i, handle)}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

const SortableRow = ({
  id,
  label,
  disabled,
  render,
}: {
  id: string | number;
  label: string;
  disabled: boolean;
  render: (dragHandle: ReactNode) => ReactNode;
}) => {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const content = render(
    disabled ? null : (
      <Icon
        {...attributes}
        {...listeners}
        aria-label={`Move ${label}`}
        style={{
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
      >
        <DragSortIcon />
      </Icon>
    )
  );
  if (content === null) return null;

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        position: 'relative',
        zIndex: isDragging ? 1 : 0,
      }}
    >
      {content}
    </div>
  );
};
